const packageName = 'googleAnalytics4';
const tagManagerUrl = 'https://www.googletagmanager.com/gtag/js';

const upsert = (map, key, update, createDefault) => {
    const currentValue = map.has(key)
        ? map.get(key)
        : createDefault();
    return map.set(key, update(currentValue));
};

const expand = (str) => str.split(',').flatMap((entry) => {
    if (!entry.includes('-')) {
        return entry;
    }
    const result = [];
    const [start, end] = entry.split('-').map(Number);
    for (let i = start; i <= end; i++) {
        result.push(i.toString());
    }
    return result;
});
/**
 * Exported from https://en.wikipedia.org/wiki/List_of_North_American_Numbering_Plan_area_codes
 *
 * In Dev Tools, select the `tbody` element containing the area codes and run the following code,
 * replacing the emdash character with a simple endash:
 *
 * ```ts
 * [...$0.querySelectorAll('td:first-child')]
 *   .filter(cell => cell.firstChild.nodeName != 'A')
 *   .map(cell => cell.textContent.trim()).join(',')
 * ```
 */
new Set([
    ...expand('200,211,221,222,230,232,233,235,237-238,241,243,244,245,247,255,257,258-259,261,265,266,271,273,274,275,277,278,280,282,283,285-287,288,290-299'),
    ...expand('300,311,322,324,327,328,333,335,338,342,344,348-349,353,355,356,357-359,362,366,369,370-379,381,382,383-384,387,388,389,390-399'),
    ...expand('400,411,420,421-422,426-427,428,429,433,439,444,446,449,451-454,455,456,457,459,460,461-462,465,466,467,471,476,477,481-483,485-486,487,488,489,490-499'),
    ...expand('511,532,535,536,537,538,542-543,545-547,549-550,552-554,555,556,558,560,565,568,569,576,578,583,589,590-599'),
    ...expand('611,621,624,625,627,632,633,634-635,637-638,642-643,644,648,652-654,655,663,665,666,668,673-676,677,679,685,686,687,688,690-699'),
    ...expand('711,722,723,729,733,735-736,739,741,744,745-746,748,749-751,752,755,756,759,761,764,766,768,776,777,783,788,789,790-799'),
    ...expand('811,821,822,823-824,827,834,836,841-842,846,851,852-853,871,874-875,879,880-887,889,890-899'),
    ...expand('911,921,922,923,924,926,927,932,933,935,942,944,946,950,953,955,957-958,960-969,974,975,976,977,981-982,987,988,990-999'),
]);

// eslint-disable-next-line @nx/enforce-module-boundaries
const initGA4 = (ID, executionContext) => {
    var _a;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
    };
    let isConsentInitialised = false;
    for (const otherProvider of ['googleAdsClicks', 'googleAnalytics4']) {
        if ((_a = executionContext.get(otherProvider)) === null || _a === void 0 ? void 0 : _a.get('gtag')) {
            isConsentInitialised = true;
            break;
        }
    }
    if (isConsentInitialised) {
        window.gtag('consent', 'update', {
            analytics_storage: 'granted',
        });
    }
    else {
        window.gtag('consent', 'default', {
            analytics_storage: 'granted',
            functional_storage: 'granted',
            personalization_storage: 'granted',
            security_storage: 'granted',
        });
    }
    upsert(executionContext, packageName, (providerMap) => providerMap.set('gtag', true), () => new Map());
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.gtag('js', new Date());
    const element = document.createElement('script');
    element.async = !0;
    element.src = `${tagManagerUrl}?id=${ID}`;
    const script = document.getElementsByTagName('script')[0];
    if (script && script.parentNode) {
        script.parentNode.insertBefore(element, script);
    }
};
const init = ({ manifest, userId, executionContext }) => {
    if (!window ||
        !manifest.variables ||
        !manifest.variables['measurementId'] ||
        manifest.variables['flowType'] === 'server') {
        return;
    }
    if (!window.google_tag_manager ||
        !window.google_tag_manager[manifest.variables['measurementId']]) {
        initGA4(manifest.variables['measurementId'], executionContext);
    }
    if (window.gtag) {
        window.gtag('config', manifest.variables['measurementId'], {
            user_id: userId,
            send_page_view: false,
        });
    }
};

const getItems = (contents = []) => {
    return contents.map((content) => {
        const data = {
            item_id: content.id.toString(),
            quantity: content.quantity || 1,
        };
        if (content.sku) {
            data['item_sku'] = content.sku;
        }
        if (content.title) {
            data['item_name'] = content.title;
        }
        if (content.category) {
            const categories = content.category.split(',');
            data['item_category'] = categories[0];
            for (let i = 1; i < Math.min(categories.length, 5); i++) {
                if (categories[i].trim()) {
                    data[`item_category${i + 1}`] =
                        categories[i].trim();
                }
            }
        }
        if (content.item_price || content.item_price === 0) {
            data['price'] = content.item_price;
        }
        if (content.brand) {
            data['item_brand'] = content.brand;
        }
        if (content.variantId) {
            data['item_variant'] = content.variantId.toString();
        }
        return data;
    });
};
const getEvent = (eventName, data, eventId) => {
    switch (eventName) {
        case 'PageView': {
            return {
                event: 'page_view',
                eventData: {},
            };
        }
        case 'ViewContent': {
            return {
                event: 'view_item',
                eventData: {
                    currency: data['currency'],
                    value: data['value'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'ViewCart': {
            return {
                event: 'view_cart',
                eventData: {
                    currency: data['currency'],
                    value: data['value'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'RemoveFromCart': {
            return {
                event: 'remove_from_cart',
                eventData: {
                    currency: data['currency'],
                    value: data['value'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'AddToWishlist': {
            return {
                event: 'add_to_wishlist',
                eventData: {
                    currency: data['currency'],
                    value: data['value'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'ViewList': {
            return {
                event: 'view_item_list',
                eventData: {
                    item_list_name: data['listName'],
                    item_list_id: data['listId'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'Login': {
            return {
                event: 'login',
                eventData: {
                    method: data['method'],
                },
            };
        }
        case 'SignUp': {
            return {
                event: 'sign_up',
                eventData: {
                    method: data['method'],
                },
            };
        }
        case 'AddToCart': {
            return {
                event: 'add_to_cart',
                eventData: {
                    currency: data['currency'],
                    value: data['value'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'InitiateCheckout': {
            return {
                event: 'begin_checkout',
                eventData: {
                    currency: data['currency'],
                    value: data['value'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'AddShippingInfo': {
            return {
                event: 'add_shipping_info',
                eventData: {
                    currency: data['currency'],
                    value: data['value'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'AddPaymentInfo': {
            return {
                event: 'add_payment_info',
                eventData: {
                    currency: data['currency'],
                    value: data['value'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'Purchase': {
            return {
                event: 'purchase',
                eventData: {
                    currency: data['currency'],
                    transaction_id: data['orderId'] || eventId,
                    value: data['value'],
                    items: getItems(data['contents']),
                },
            };
        }
        case 'Search': {
            return {
                event: 'search',
                eventData: {
                    search_term: data['search'],
                },
            };
        }
        case 'Lead': {
            return {
                event: 'generate_lead',
                eventData: {
                    currency: data['currency'],
                    value: data['value'],
                },
            };
        }
        default: {
            return {
                event: eventName,
                eventData: data,
            };
        }
    }
};
const handleTag = ({ data = {}, eventName, eventId, manifestVariables, }) => {
    if (!eventName ||
        !window.gtag ||
        !(manifestVariables === null || manifestVariables === void 0 ? void 0 : manifestVariables['measurementId']) ||
        manifestVariables['flowType'] === 'server') {
        return;
    }
    const { event, eventData } = getEvent(eventName, data, eventId);
    eventData['send_to'] = manifestVariables['measurementId'];
    if (manifestVariables['debug'] === '1') {
        eventData['debug_mode'] = true;
    }
    window.gtag('event', event, { ...eventData, app: 'blotout' });
};

const getCookieValue = (key) => {
    var _a;
    try {
        if (!document || !document.cookie) {
            return '';
        }
        const cookies = parseCookies(document.cookie);
        return (_a = cookies[key]) !== null && _a !== void 0 ? _a : '';
    }
    catch {
        return '';
    }
};
const parseCookies = (cookie) => {
    return Object.fromEntries(cookie
        .split(/;\s+/)
        .map((r) => r.split('=').map((str) => str.trim()))
        .map(([cookieKey, ...cookieValues]) => {
        const cookieValue = cookieValues.join('=');
        if (!cookieKey) {
            return [];
        }
        let decodedValue = '';
        if (cookieValue) {
            try {
                decodedValue = decodeURIComponent(cookieValue);
            }
            catch (e) {
                console.log(`Unable to decode cookie ${cookieKey}: ${e}`);
                decodedValue = cookieValue;
            }
        }
        return [cookieKey, decodedValue];
    }));
};

const tag = ({ data, eventName, manifestVariables, eventId }) => {
    let isLoaded;
    if (manifestVariables['flowType'] === 'browser') {
        isLoaded = true;
    }
    else if (manifestVariables['flowType'] === 'server') {
        isLoaded = false;
    }
    else {
        isLoaded = window && window.gtag && !!getCookieValue('_ga');
    }
    if (isLoaded) {
        handleTag({ data, eventName, manifestVariables, eventId });
    }
    return {
        loaded: isLoaded,
        sdkVersion: "0.71.0" ,
    };
};

const consent = ({ hasConsent }) => {
    if (!(window === null || window === void 0 ? void 0 : window.gtag)) {
        return;
    }
    window.gtag('consent', 'update', {
        analytics_storage: hasConsent ? 'granted' : 'denied',
        functional_storage: 'granted',
        personalization_storage: 'granted',
        security_storage: 'granted',
    });
};

// eslint-disable-next-line @nx/enforce-module-boundaries
const data = {
    name: packageName,
    init,
    tag,
    consent,
};
try {
    if (window) {
        if (!window.edgetagProviders) {
            window.edgetagProviders = [];
        }
        window.edgetagProviders.push(data);
    }
}
catch {
    // No window
}

export { data as default };
